import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Plus, 
  Calendar, 
  Mail, 
  Phone, 
  Building, 
  User,
  AlertCircle,
  MoreVertical,
  Search,
  Filter
} from 'lucide-react';
import { MatterList, MatterForm, MatterStats } from '../components/matters';
import { useClientDetails } from '../hooks/useClientDetails';
import { useMatters } from '../hooks/useMatters';
import { usePageNavigation } from '../contexts/NavigationContext';
import { useToast } from '../components/ui/Toast';
import type { Matter, MatterFormData, UpdateMatterRequest } from '../types';

const ClientDetails: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageNavigation } = usePageNavigation();
  
  // State
  const [activeTab, setActiveTab] = useState('matters');
  const [showMatterForm, setShowMatterForm] = useState(false);
  const [editingMatter, setEditingMatter] = useState<Matter | undefined>();
  const [searchTerm, setSearchTerm] = useState('');

  // Hooks
  const { 
    clientDetails, 
    isLoading: clientLoading, 
    error: clientError, 
    loadClientDetails 
  } = useClientDetails();

  const {
    matters,
    isLoading: mattersLoading,
    loadMatters,
    createMatter,
    updateMatter,
    deleteMatter
  } = useMatters(clientId || '');

  const { success, error } = useToast();

  // Load data on mount
  useEffect(() => {
    if (clientId) {
      loadClientDetails(clientId);
      loadMatters();
    }
  }, [clientId]); // Only depend on clientId to prevent infinite loops

  // Set up navigation breadcrumbs when client data loads
  useEffect(() => {
    if (clientDetails) {
      setPageNavigation(`${clientDetails.name}`, [
        { label: 'Dashboard', href: '/' },
        { label: 'Clients', href: '/clients' },
        { label: clientDetails.name, href: `/clients/${clientDetails.id}` }
      ]);
    } else if (clientId) {
      // Set loading state breadcrumb
      setPageNavigation('Client Details', [
        { label: 'Dashboard', href: '/' },
        { label: 'Clients', href: '/clients' },
        { label: 'Client Details' }
      ], true);
    }
  }, [clientDetails, clientId, setPageNavigation]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/clients');
  };

  // Handle matter form submission
  const handleMatterSubmit = async (formData: MatterFormData | UpdateMatterRequest) => {
    if (!clientId) return;

    try {
      if (editingMatter) {
        // Update existing matter
        await updateMatter(clientId, editingMatter.id, formData as UpdateMatterRequest);
        success('Matter Updated', `"${formData.title}" has been updated successfully.`);
      } else {
        // Create new matter
        await createMatter(clientId, formData as MatterFormData);
        success('Matter Created', `"${formData.title}" has been created successfully.`);
      }
      
      setShowMatterForm(false);
      setEditingMatter(undefined);
    } catch (err) {
      console.error('Matter operation failed:', err);
      const operation = editingMatter ? 'update' : 'create';
      error(
        `Failed to ${operation} matter`, 
        `There was an error ${operation === 'update' ? 'updating' : 'creating'} the matter. Please try again.`
      );
    }
  };

  // Handle matter form cancel
  const handleMatterFormCancel = () => {
    setShowMatterForm(false);
    setEditingMatter(undefined);
  };

  // Handle edit matter
  const handleEditMatter = (matter: Matter) => {
    setEditingMatter(matter);
    setShowMatterForm(true);
  };

  // Handle add new matter
  const handleAddMatter = () => {
    setEditingMatter(undefined);
    setShowMatterForm(true);
  };

  // Handle delete matter
  const handleDeleteMatter = async (matter: Matter) => {
    if (!clientId) return;

    const confirmMessage = `Are you sure you want to delete the matter "${matter.title}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteMatter(clientId, matter.id);
      success('Matter Deleted', `"${matter.title}" has been deleted successfully.`);
    } catch (err) {
      console.error('Delete matter failed:', err);
      error(
        'Failed to delete matter',
        'There was an error deleting the matter. Please try again.'
      );
    }
  };

  // Handle matter click - navigate to matter details
  const handleMatterClick = (matter: Matter) => {
    navigate(`/matters/${matter.id}`);
  };

  // Filter matters based on search
  const filteredMatters = matters.filter(matter =>
    matter.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    matter.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    matter.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading state
  if (clientLoading || !clientDetails) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading client details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (clientError) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">{clientError}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'matters', label: 'Matters', count: matters.length },
    { id: 'documents', label: 'Documents', count: 0 },
    { id: 'billing', label: 'Billing', count: 0 },
    { id: 'notes', label: 'Notes', count: 0 },
    { id: 'activity', label: 'Activity', count: 0 }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header with Back Button */}
      <header className="bg-gray-800 shadow-sm border-b border-gray-700">
        <div className="px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-white">Client Details</h2>
            </div>
          </div>
        </div>
      </header>

      {/* Client Header Section */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="px-6 py-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-6">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">{clientDetails.name}</h1>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 mt-2">
                  <div className="flex items-center space-x-2 text-gray-300">
                    <Mail className="w-4 h-4" />
                    <span>{clientDetails.email}</span>
                  </div>
                  {clientDetails.phone && (
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Phone className="w-4 h-4" />
                      <span>{clientDetails.phone}</span>
                    </div>
                  )}
                  {clientDetails.company && (
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Building className="w-4 h-4" />
                      <span>{clientDetails.company}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-gray-300">
                    <Calendar className="w-4 h-4" />
                    <span>Client since {new Date(clientDetails.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                <Edit className="w-4 h-4" />
                <span>Edit Client</span>
              </button>
              <button className="text-gray-400 hover:text-white p-2 rounded-lg transition-colors">
                <MoreVertical className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Matter Statistics - Replaces Client Statistics */}
          <div className="mt-6">
            <MatterStats matters={matters} isLoading={mattersLoading} />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="px-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-2 rounded-full px-2.5 py-0.5 text-xs ${
                    activeTab === tab.id 
                      ? 'bg-blue-900 text-blue-300' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-6">
        {activeTab === 'matters' && (
          <div className="space-y-6">
            {/* Matter Search and Filter Bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search matters..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button className="flex items-center space-x-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors">
                  <Filter className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
              
              <button
                onClick={handleAddMatter}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Matter</span>
              </button>
            </div>

            {/* Matter List */}
            <MatterList
              matters={filteredMatters}
              isLoading={mattersLoading}
              onMatterClick={handleMatterClick}
              onEditMatter={handleEditMatter}
              onDeleteMatter={handleDeleteMatter}
              className="mt-6"
            />

            {/* Matter Form Modal */}
            {showMatterForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="w-full max-w-2xl">
                  <MatterForm
                    matter={editingMatter}
                    clientId={clientId} // Pre-select the current client
                    isLoading={mattersLoading}
                    onSubmit={handleMatterSubmit}
                    onCancel={handleMatterFormCancel}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Placeholder content for other tabs */}
        {activeTab !== 'matters' && (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              {tabs.find(t => t.id === activeTab)?.label} - Coming Soon
            </h3>
            <p className="text-gray-500">
              This section will be implemented in future updates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetails;
